// src/features/goal/services/objectiveService.ts
// Objective 前端服務層 - 封裝所有 Objective 相關的 API 調用

import { functions } from '../../../config/firebase';
import { httpsCallable } from 'firebase/functions';
import {
  Objective,
  ObjectiveQueryOptions,
  CreateObjectiveRequest,
  UpdateObjectiveRequest,
  L1ObjectiveMemory,
  ObjectiveCategory
} from '../types/objective';
import { logger } from '../../../utils/logger';

/**
 * API 響應類型定義
 */
interface CreateObjectiveResponse {
  success: boolean;
  objectiveId: string;
  message: string;
}

interface GetObjectivesResponse {
  success: boolean;
  objectives: Objective[];
  count: number;
}

interface UpdateObjectiveResponse {
  success: boolean;
  message: string;
}

interface DeleteObjectiveResponse {
  success: boolean;
  message: string;
}

interface GetL1MemoriesResponse {
  success: boolean;
  memories: (L1ObjectiveMemory & { hasActiveObjective: boolean })[];
  count: number;
}

export interface GoalBreakdownTaskSuggestion {
  title: string;
  priority: string;
  notes?: string;
  category?: string;
  energyLevel?: string;
}

interface BreakdownGoalToTasksResponse {
  tasks: GoalBreakdownTaskSuggestion[];
  objectiveId: string;
  objectiveTitle: string;
}

/**
 * Objective Service
 * 提供前端與後端 Objective API 的交互接口
 */
class ObjectiveService {

  /**
   * 創建新的 Objective
   */
  static async createObjective(request: CreateObjectiveRequest): Promise<string> {
    try {
      logger.dev('ObjectiveService: Creating objective', {
        title: request.title,
        status: request.status,
        hasL1Link: !!request.linkedObjectiveId
      });

      const createObjectiveFunc = httpsCallable<CreateObjectiveRequest, CreateObjectiveResponse>(
        functions,
        'createObjective'
      );

      const result = await createObjectiveFunc(request);

      if (!result.data.success) {
        throw new Error(result.data.message || 'Failed to create objective');
      }

      logger.info('ObjectiveService: Objective created successfully', {
        objectiveId: result.data.objectiveId
      });

      return result.data.objectiveId;

    } catch (error) {
      logger.error('ObjectiveService: Failed to create objective', error);
      throw this.handleError(error);
    }
  }

  /**
   * 獲取 Objective 列表
   */
  static async getObjectives(options?: ObjectiveQueryOptions): Promise<Objective[]> {
    try {
      logger.dev('ObjectiveService: Fetching objectives', options);

      const getObjectivesFunc = httpsCallable<ObjectiveQueryOptions, GetObjectivesResponse>(
        functions,
        'getObjectives'
      );

      const result = await getObjectivesFunc(options || {});

      if (!result.data.success) {
        throw new Error('Failed to fetch objectives');
      }

      logger.info('ObjectiveService: Objectives fetched successfully', {
        count: result.data.count
      });

      return result.data.objectives;

    } catch (error) {
      logger.error('ObjectiveService: Failed to fetch objectives', error);
      throw this.handleError(error);
    }
  }

  /**
   * 獲取活躍的 Objectives
   */
  static async getActiveObjectives(): Promise<Objective[]> {
    return this.getObjectives({
      status: 'active',
      orderBy: 'totalRipples',
      orderDirection: 'desc',
      limit: 20
    });
  }

  /**
   * 獲取活躍的 Objectives 數量
   * 用於創建新 Objective 時檢查是否超過 3 個限制
   */
  static async getActiveObjectivesCount(): Promise<number> {
    try {
      const activeObjectives = await this.getActiveObjectives();
      return activeObjectives.length;
    } catch (error) {
      logger.error('ObjectiveService: Failed to get active count', error);
      return 0;  // 發生錯誤時返回 0，允許創建
    }
  }

  /**
   * 獲取所有非歸檔的 Objectives
   *
   * 不傳遞 status 參數，讓後端決定「非歸檔」的定義
   * 這樣避免邏輯洩漏到前端
   */
  static async getAllNonArchivedObjectives(): Promise<Objective[]> {
    return this.getObjectives({
      // 不傳遞 status - 讓後端定義「非歸檔」的含義
      orderBy: 'createdAt',
      orderDirection: 'desc',
      limit: 50
    });
  }

  /**
   * Goal → Tasks AI 拆解
   * 請 AI 對指定 Objective 建議 2-3 個具體可執行的 Tasks
   *
   * @param objectiveId - Objective ID
   * @returns 建議的 Task 列表（用戶確認後才建立）
   */
  static async breakdownGoalToTasks(
    objectiveId: string
  ): Promise<BreakdownGoalToTasksResponse> {
    try {
      const fn = httpsCallable<
        { objectiveId: string },
        BreakdownGoalToTasksResponse
      >(functions, 'breakdownGoalToTasks');

      const result = await fn({ objectiveId });
      return result.data;
    } catch (error) {
      logger.error('ObjectiveService: breakdownGoalToTasks failed', error);
      throw this.handleError(error);
    }
  }

  /**
   * 更新 Objective
   */
  static async updateObjective(
    objectiveId: string,
    updates: UpdateObjectiveRequest
  ): Promise<void> {
    try {
      logger.dev('ObjectiveService: Updating objective', {
        objectiveId,
        updates: Object.keys(updates)
      });

      const updateObjectiveFunc = httpsCallable<
        { objectiveId: string } & UpdateObjectiveRequest,
        UpdateObjectiveResponse
      >(functions, 'updateObjective');

      const result = await updateObjectiveFunc({
        objectiveId,
        ...updates
      });

      if (!result.data.success) {
        throw new Error(result.data.message || 'Failed to update objective');
      }

      logger.info('ObjectiveService: Objective updated successfully', {
        objectiveId
      });

    } catch (error) {
      logger.error('ObjectiveService: Failed to update objective', error);
      throw this.handleError(error);
    }
  }

  /**
   * 刪除 Objective（軟刪除，改為 archived 狀態）
   */
  static async deleteObjective(objectiveId: string): Promise<void> {
    try {
      logger.dev('ObjectiveService: Deleting objective', { objectiveId });

      const deleteObjectiveFunc = httpsCallable<
        { objectiveId: string },
        DeleteObjectiveResponse
      >(functions, 'deleteObjective');

      const result = await deleteObjectiveFunc({ objectiveId });

      if (!result.data.success) {
        throw new Error(result.data.message || 'Failed to delete objective');
      }

      logger.info('ObjectiveService: Objective deleted successfully', {
        objectiveId
      });

    } catch (error) {
      logger.error('ObjectiveService: Failed to delete objective', error);
      throw this.handleError(error);
    }
  }

  /**
   * 暫停 Objective
   */
  static async pauseObjective(objectiveId: string): Promise<void> {
    return this.updateObjective(objectiveId, { status: 'paused' });
  }

  /**
   * 恢復 Objective
   */
  static async resumeObjective(objectiveId: string): Promise<void> {
    return this.updateObjective(objectiveId, { status: 'active' });
  }

  /**
   * 完成 Objective
   */
  static async completeObjective(objectiveId: string): Promise<void> {
    return this.updateObjective(objectiveId, { status: 'completed' });
  }

  /**
   * 獲取 L1 Objective Memories（用於創建 Objective）
   */
  static async getL1ObjectiveMemories(): Promise<(L1ObjectiveMemory & { hasActiveObjective: boolean })[]> {
    try {
      logger.dev('ObjectiveService: Fetching L1 objective memories');

      const getL1MemoriesFunc = httpsCallable<void, GetL1MemoriesResponse>(
        functions,
        'getL1ObjectiveMemories'
      );

      const result = await getL1MemoriesFunc();

      if (!result.data.success) {
        throw new Error('Failed to fetch L1 memories');
      }

      logger.info('ObjectiveService: L1 memories fetched successfully', {
        count: result.data.count
      });

      return result.data.memories;

    } catch (error) {
      logger.error('ObjectiveService: Failed to fetch L1 memories', error);
      throw this.handleError(error);
    }
  }

  /**
   * 錯誤處理
   */
  private static handleError(error: unknown): Error {
    const firebaseError = error as { code?: string; message?: string };
    if (firebaseError.code) {
      // Firebase Functions 錯誤
      switch (firebaseError.code) {
        case 'unauthenticated':
          return new Error('Please sign in to continue');
        case 'permission-denied':
          return new Error('You do not have permission to perform this action');
        case 'invalid-argument':
          return new Error(firebaseError.message || 'Invalid request parameters');
        case 'already-exists':
          return new Error(firebaseError.message || 'This resource already exists');
        case 'not-found':
          return new Error('The requested resource was not found');
        default:
          return new Error(firebaseError.message || 'Something went wrong. Please try again.');
      }
    }

    if (error instanceof Error) {
      return error;
    }

    return new Error('An unknown error occurred');
  }
}

export default ObjectiveService;

// 具名導出靜態方法，方便直接使用
export const {
  getActiveObjectives,
  getObjectives,
  createObjective,
  updateObjective,
  deleteObjective,
  pauseObjective,
  resumeObjective,
  completeObjective,
  getL1ObjectiveMemories,
  getActiveObjectivesCount,
  getAllNonArchivedObjectives,
  breakdownGoalToTasks,
} = ObjectiveService;
